import * as d3 from "d3";
import dayjs from "dayjs";
import {
  debounce,
  each,
  filter,
  get,
  indexOf,
  isEmpty,
  isNil,
  isObject,
  keys,
  last,
  sortBy,
  values,
} from "lodash-es";

import {
  Component,
  getAssetPath,
  h,
  Host,
  Listen,
  Prop,
  State,
  Element,
} from "@stencil/core";

import { getStore, ILiftStatusStore } from "../../store";
import { sensorInfo } from "../../utils/sensorInfo";
import {
  ILiftStatusSensorDataResponse,
  ILiftStatusSensorResponse,
  ILiftStatusWebComponentError,
} from "../../utils/types";
import { watchStoreLocale } from "../../utils/locale-state";
import { getStoreUnavailableError } from "../../utils/store-error";
import { ErrorType } from "../../utils/status";
import {
  applyErrorMessage,
  clearErrorMessage,
  getErrorMessageForDisplay,
} from "../../utils/error-message";
import {
  DoorPosition,
  HealthStatus,
  MovementState,
  SENSOR,
  SWIMLANE_SENSOR_IDS,
  getHealthStatus,
  isInvertedCheckSensor,
  isNormalCheckSensor,
  isZeroBasedStopSensor,
  sensorIdsToString,
} from "../../utils/sensors";

interface IChartDataItem {
  start: Date;
  end: Date;
  index: number;
  color: string;
  value: string | number;
}

interface ISwimLaneSettings {
  minHeight: number;
  laneHeight: number;
  timer: NodeJS.Timeout | number | null;
  subscriptions: unknown[];
  swimlanes: {
    [key: number]: d3.Selection<SVGGElement, unknown, null, undefined>;
  };
  labels: Array<{
    element: d3.Selection<SVGGElement, unknown, null, undefined>;
    index: number;
  }>;
  scale: {
    units: string;
    value: number;
  };
}

@Component({
  tag: "ls-swimlane",
  styleUrl: "ls-swimlane.css",
  shadow: true,
  assetsDirs: ["assets"],
})
export class LsSwimlane {
  /**
   * Specifies the IDs of the sensors that are to be displayed.
   */
  @Prop() readonly sensorIds = sensorIdsToString(SWIMLANE_SENSOR_IDS);

  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  /**
   * The sensor IDs input property, converted to an array of numbers.
   */
  @State() sensorIdsArray = this.sensorIds
    .split(",")
    .filter((id) => id.trim() !== "")
    .map((id) => parseInt(id.trim(), 10));

  /**
   * Centralized error state for tracking component errors
   */
  @State() errorState: ILiftStatusWebComponentError = {
    hasError: false,
    messageKey: undefined,
    type: ErrorType.None,
  };

  /**
   * Component loading state
   */
  @State() isLoading = true;

  private store: ILiftStatusStore | null = null;

  /**
   * The Host element
   */
  @Element() element: HTMLLsSwimlaneElement;

  // Listen for resize events and recalculate
  @Listen("resize", { target: "window" })
  resizeWindow() {
    // If the component is in loading or error state, we don't want to trigger a render of the graph
    if (this.errorState.hasError || this.isLoading) return;

    this.rebuildSwimlaneOnResize();
  }

  private readonly DEBOUNCEDELAY = 350;
  private readonly MARGIN = { BOTTOM: 0, LEFT: 10, RIGHT: 10, TOP: 0 };

  // Using a debounce here to save resources
  private rebuildSwimlaneOnResize = debounce(
    () => {
      // Redraw the chart
      this.drawChart();
      // Rebuild chart data elements with new dimensions
      this.rebuildChartData();
      this.updateTimeline();
    },
    this.DEBOUNCEDELAY,
    // Enables the first trigger i.e. for when the screen gets maximized or media queries are triggered
    { leading: true }
  );

  private canvas: HTMLDivElement | null = null;
  private swimLineWidth = 0;
  private sensorLabelsWidth = 0;
  private xAxisScale: d3.ScaleTime<number, number>;
  private dateRange: { from: Date | null; to: Date | null } = {
    from: null,
    to: null,
  };
  private xAxis: d3.Axis<Date | d3.NumberValue> | undefined;
  private interval: NodeJS.Timeout | number;
  private chartDataCache: IChartDataItem[] = [];

  private storedAndOrderedSensorData: {
    [key: string]: {
      value: string | number;
      start: number;
      end?: number;
      color: string;
    }[];
  } = {};

  /**
   * Settings object with some values
   */
  private swimLaneSettings: ISwimLaneSettings = {
    minHeight: 200,
    laneHeight: 25,
    timer: null,
    subscriptions: [],
    swimlanes: {},
    labels: [],
    scale: {
      units: "minute",
      value: 1,
    },
  };

  private storeUnsubscribes: Array<() => void> = [];

  /**
   * Get display symbol for sensor value
   */
  private getSensorDisplaySymbol = (
    value: string | number,
    sensorId: number
  ): string | number => {
    // Check sensors: ✓ for the healthy state, ✗ for the unhealthy state.
    // Inverted check sensors swap the meaning of healthy/unhealthy.
    if (isNormalCheckSensor(sensorId) || isInvertedCheckSensor(sensorId)) {
      const status = getHealthStatus(value);
      if (status !== HealthStatus.NotConnected) {
        const isGood =
          (status === HealthStatus.Healthy) !== isInvertedCheckSensor(sensorId);
        return isGood ? "✓" : "✗";
      }
    }

    // Direction arrows
    if (value === MovementState.Down) return "↓";
    if (value === MovementState.Up) return "↑";

    // Default: return original value
    return value;
  };

  /**
   * Door position values that are rendered as an icon rather than as text.
   */
  private static readonly DOOR_ICON_POSITIONS: string[] = [
    DoorPosition.Open,
    DoorPosition.Closed,
    DoorPosition.Locked,
    DoorPosition.Closing,
    DoorPosition.Opening,
  ];

  /**
   * Sensor color configuration
   */
  private static readonly SENSOR_COLORS = {
    healthy: "#BBF7D0",
    neutral: "#E4E4E7",
    unhealthy: "#FECACA",
    // Colors for door position / movement state values
    values: {
      [DoorPosition.Opening]: "#FEF08A",
      [MovementState.Up]: "#FEF08A",
      [DoorPosition.Closing]: "#E9D5FF",
      [MovementState.Down]: "#E9D5FF",
      [DoorPosition.Open]: "#BBF7D0",
      [DoorPosition.Closed]: "#FECACA",
      [DoorPosition.Locked]: "#E4E4E7",
    } as Record<string, string>,
    // Default colors
    numeric: "#C7D2FE",
    lastPassedStop: { even: "#CFFAFE", odd: "#A5F3FC" },
    fallback: "transparent",
  };

  /**
   * Create fixed colors for sensor ID's
   */
  private sensorValueColorCode = (value: string | number, sensorId: number) => {
    const { SENSOR_COLORS } = LsSwimlane;

    // Normal check sensors (green when healthy, gray when unhealthy)
    if (isNormalCheckSensor(sensorId)) {
      const status = getHealthStatus(value);
      if (status === HealthStatus.Healthy) return SENSOR_COLORS.healthy;
      if (status === HealthStatus.Unhealthy) return SENSOR_COLORS.neutral;
      return SENSOR_COLORS.fallback;
    }

    // Inverted check sensors (red when healthy, green when unhealthy)
    if (isInvertedCheckSensor(sensorId)) {
      const status = getHealthStatus(value);
      if (status === HealthStatus.Healthy) return SENSOR_COLORS.unhealthy;
      if (status === HealthStatus.Unhealthy) return SENSOR_COLORS.healthy;
      return SENSOR_COLORS.fallback;
    }

    // Door position / movement state values
    if (typeof value === "string" && value in SENSOR_COLORS.values) {
      return SENSOR_COLORS.values[value];
    }

    // Handle numeric values
    const numeric = typeof value === "number" ? value : parseInt(value, 10);
    if (!Number.isNaN(numeric)) {
      if (sensorId === SENSOR.LastPassedStop) {
        return numeric % 2 === 0
          ? SENSOR_COLORS.lastPassedStop.even
          : SENSOR_COLORS.lastPassedStop.odd;
      }
      return SENSOR_COLORS.numeric;
    }

    return SENSOR_COLORS.fallback;
  };

  /**
   * Truncate text to fit within the available width and add ellipsis if needed
   * We can't use CSS text-overflow: ellipsis here unfortunately
   */
  private truncateText = (
    textElement: d3.Selection<SVGTextElement, unknown, null, undefined>,
    maxWidth: number
  ): void => {
    const originalText = textElement.text();
    const ellipsis = "...";

    // If text fits, no need to truncate
    if ((textElement.node()?.getBBox().width || 0) <= maxWidth) {
      return;
    }

    // Binary search for the maximum length that fits
    let low = 0;
    let high = originalText.length;

    while (low < high) {
      const mid = Math.floor((low + high + 1) / 2);
      const truncated = originalText.substring(0, mid) + ellipsis;

      textElement.text(truncated);
      const width = textElement.node()?.getBBox().width || 0;

      if (width <= maxWidth) {
        low = mid;
      } else {
        high = mid - 1;
      }
    }

    // Set final truncated text
    textElement.text(originalText.substring(0, low) + ellipsis);
  };

  private clearChart = () => {
    // clear out the html of the chart so that we have a clear chart
    d3.select(this.canvas).html("");
  };

  /**
   * Create initial chart layout
   */
  private drawChart = () => {
    if (this.canvas === null || this.canvas === undefined) {
      return;
    }

    // lets first remove the current chart before we draw a new one to prevent overlapping
    this.clearChart();

    // (Re)calculate width and height
    const componentWidth =
      this.element.clientWidth <= 0 ? 1 : this.element.clientWidth;
    const availableWidth =
      componentWidth - this.MARGIN.LEFT - this.MARGIN.RIGHT;
    const width = Math.max(100, availableWidth);
    this.sensorLabelsWidth =
      width < 200 ? width * 0.3 : width < 400 ? width * 0.35 : width * 0.25;
    this.swimLineWidth = width - this.sensorLabelsWidth;

    // Calculate the exact height needed upfront to prevent scrollbars
    const totalHeight =
      this.sensorIdsArray.length * this.swimLaneSettings.laneHeight + 23;

    // Chart
    const chart = d3
      .select(this.canvas)
      .append("svg")
      .attr("viewBox", `0 0 ${componentWidth} ${totalHeight}`)
      .attr("width", "100%")
      .attr("height", totalHeight)
      .attr("class", "chart");

    // Main group with margin transform
    const mainGroup = chart
      .append("g")
      .attr("transform", `translate(${this.MARGIN.LEFT}, ${this.MARGIN.TOP})`);

    // Swimlanes
    const swimlanes = mainGroup
      .append("g")
      .attr("width", this.swimLineWidth)
      .attr("transform", `translate(${Math.floor(this.sensorLabelsWidth)},0)`)
      .attr("class", "swimlanes");

    // Sensor
    const sensorLabels = mainGroup
      .append("g")
      .attr("width", this.sensorLabelsWidth)
      .attr("class", "sensors-labels");

    // Sensor background
    sensorLabels
      .append("rect")
      .attr("width", this.sensorLabelsWidth)
      .attr("height", "100%")
      .style("fill", "transparent");

    // Horizontal and vertical scales
    const endDate = dayjs().toDate();
    const startDate = dayjs()
      .subtract(
        this.swimLaneSettings.scale.value as number,
        this.swimLaneSettings.scale.units as dayjs.ManipulateType
      )
      .toDate();
    const horizontalScale = d3
      .scaleTime()
      .domain([startDate, endDate])
      .range([0, this.swimLineWidth]);

    this.xAxisScale = horizontalScale;
    this.dateRange.from = startDate;
    this.dateRange.to = endDate;

    // Add the x-axis
    const horizontalAxis = d3
      .axisBottom(horizontalScale)
      .ticks(5, "s")
      .tickFormat(d3.timeFormat("%H:%M:%S"))
      .tickSize(6);

    // Append the labels to the x axis
    mainGroup
      .append("g")
      .attr(
        "transform",
        `translate(${Math.floor(this.sensorLabelsWidth)},${
          this.sensorIdsArray.length * this.swimLaneSettings.laneHeight
        })`
      )
      .attr("class", "main axis time")
      .call(horizontalAxis)
      .selectAll("text")
      .attr("dx", 5)
      .attr("dy", 12);

    this.xAxis = horizontalAxis;

    swimlanes.append("g").attr("class", "paths");

    // Add swimlane for each sensor
    each(this.sensorIdsArray, (sensorId) => {
      this.addSwimlane(sensorId);
    });
  };

  /**
   * Add swimlane to chart
   */
  private addSwimlane = (sensorId: number) => {
    if (this.xAxis !== undefined) {
      // Only add swimlanes for sensors that we support
      if (indexOf(this.sensorIdsArray, sensorId) > -1) {
        const indexHeight =
          this.swimLaneSettings.laneHeight *
          this.sensorIdsArray.indexOf(sensorId);

        // Put the ticks (date and time) at the bottom
        const chart = d3.select(this.canvas);

        // Add swimlane group
        const swimlaneGroup = chart
          .select(".swimlanes")
          .append("g")
          .attr("width", this.swimLineWidth)
          .attr("height", this.swimLaneSettings.laneHeight)
          .attr("class", "swimlane")
          .attr("transform", `translate(0,${indexHeight})`);

        // Add label group
        const labelGroup = chart
          .select(".sensors-labels")
          .append("g")
          .attr("width", this.sensorLabelsWidth)
          .attr("height", this.swimLaneSettings.laneHeight)
          .attr("class", "sensor")
          .attr("transform", `translate(0,${indexHeight})`);

        // Add text label
        const labelTextElement = labelGroup
          .append("text")
          .text(sensorInfo.getLabel(sensorId, this.store?.state.locale))
          .attr(
            "transform",
            `translate(0,${this.swimLaneSettings.laneHeight - 9})`
          )
          .attr("font-size", "10")
          .attr("class", "sensor-title");

        // Truncate text if it's too long for the available space
        this.truncateText(labelTextElement, this.sensorLabelsWidth - 10);

        // Add lines
        swimlaneGroup
          .append("line")
          .attr("x1", 0)
          .attr("y1", this.swimLaneSettings.laneHeight)
          .attr("x2", this.swimLineWidth)
          .attr("y2", this.swimLaneSettings.laneHeight)
          .attr("stroke", "lightgray");

        labelGroup
          .append("line")
          .attr("x1", 0)
          .attr("y1", this.swimLaneSettings.laneHeight)
          .attr("x2", this.sensorLabelsWidth)
          .attr("y2", this.swimLaneSettings.laneHeight)
          .attr("stroke", "lightgray");

        // Add the swimlane
        this.swimLaneSettings.swimlanes[sensorId] = swimlaneGroup;

        this.swimLaneSettings.labels.push({
          element: labelGroup,
          index: indexOf(this.sensorIdsArray, sensorId),
        });
      }
    }
  };

  /**
   * Add data to state
   */
  private addData = (
    sensorId: number,
    value: string | number,
    timestamp: number
  ) => {
    if (isNil(sensorId) || !this.sensorIdsArray.includes(sensorId)) return;

    // Get or initialize sensor data collection
    const sensorDataCollection =
      this.storedAndOrderedSensorData[`${sensorId}`] || [];
    const lastValue = last(sensorDataCollection);

    // If value changed, close previous entry and add new one
    if (lastValue?.value !== value) {
      // Close previous entry if it exists
      if (!isNil(lastValue)) {
        sensorDataCollection[sensorDataCollection.length - 1] = {
          ...lastValue,
          end: timestamp,
        };
      }

      // Add new entry
      sensorDataCollection.push({
        value,
        start: timestamp,
        color: "#eee", // Color will be overwritten
      });

      // Update stored data and trigger rebuild
      this.storedAndOrderedSensorData[`${sensorId}`] = sortBy(
        sensorDataCollection,
        "start"
      );
      this.rebuildChartData();
    }
  };

  private updateTimeline = () => {
    if (this.canvas === null) {
      return;
    }

    const currentTime = dayjs();
    const theLimit = currentTime
      .clone()
      .subtract(
        this.swimLaneSettings.scale.value,
        this.swimLaneSettings.scale.units as dayjs.ManipulateType
      );

    // Rebuild chart data with current time to extend current values
    this.updateChartDataWithCurrentTime(currentTime);

    // Only update scale domain - this is the smooth scrolling part
    this.xAxisScale?.domain([theLimit.toDate(), currentTime]);

    // Update the axis
    const chart = d3.select(this.canvas);
    (
      chart.select(".main.axis.time") as d3.Selection<
        SVGGElement,
        unknown,
        null,
        undefined
      >
    ).call(this.xAxis!);

    // Update transforms of existing items for smooth scrolling
    const swimlaneGroup = chart.select(".swimlanes");
    const itemGroups = swimlaneGroup.selectAll("g.mainItemGroup");

    // Remove items that are completely past the time limit from DOM
    itemGroups
      .filter((d: IChartDataItem) => {
        const theEnd = d.end.getTime() === 0 ? currentTime : dayjs(d.end);
        return theLimit.isAfter(theEnd);
      })
      .remove();

    // Update transforms for remaining items
    itemGroups
      .filter((d: IChartDataItem) => {
        const theEnd = d.end.getTime() === 0 ? currentTime : dayjs(d.end);
        return !theLimit.isAfter(theEnd);
      })
      .attr("transform", (d: IChartDataItem) => {
        let theBegin = new Date(d.start);
        if (theLimit.isAfter(theBegin)) {
          theBegin = theLimit.toDate();
        }
        return `translate( ${this.xAxisScale(theBegin)},${
          this.swimLaneSettings.laneHeight * d.index + 2
        })`;
      });

    // Update widths of rectangles for remaining items
    swimlaneGroup
      .selectAll("rect")
      .filter((d: IChartDataItem) => {
        const theEnd = d.end.getTime() === 0 ? currentTime : dayjs(d.end);
        return !theLimit.isAfter(theEnd);
      })
      .attr("width", (d: IChartDataItem) => {
        // For current values (end = 0), extend to current time
        const theEnd = d.end.getTime() === 0 ? currentTime : dayjs(d.end);
        let theBegin = new Date(d.start);

        if (theLimit.isAfter(theBegin)) {
          theBegin = theLimit.toDate();
        }

        let result = this.xAxisScale(theEnd) - this.xAxisScale(theBegin);
        if (result < 1) {
          result = 5;
        }
        return result;
      });

    // Update text positions for remaining items
    swimlaneGroup
      .selectAll("text.sensor-value")
      .filter((d: IChartDataItem) => {
        const theEnd = d.end.getTime() === 0 ? currentTime : dayjs(d.end);
        return !theLimit.isAfter(theEnd);
      })
      .attr("transform", (d: IChartDataItem) => {
        // For current values (end = 0), extend to current time
        const theEnd = d.end.getTime() === 0 ? currentTime : dayjs(d.end);
        let theBegin = new Date(d.start);

        if (theLimit.isAfter(theBegin)) {
          theBegin = theLimit.toDate();
        }

        const width = this.xAxisScale(theEnd) - this.xAxisScale(theBegin);
        const xPosition = Math.max(width / 2, 6);
        return `translate(${xPosition},0)`;
      });

    // Update image positions for remaining items at same frequency as other elements for smooth movement
    swimlaneGroup
      .selectAll("image")
      .filter((d: IChartDataItem) => {
        const theEnd = d.end.getTime() === 0 ? currentTime : dayjs(d.end);
        return !theLimit.isAfter(theEnd);
      })
      .style("transform", (d: IChartDataItem) => {
        // For current values (end = 0), extend to current time
        const theEnd = d.end.getTime() === 0 ? currentTime : dayjs(d.end);
        let theBegin = new Date(d.start);

        if (theLimit.isAfter(theBegin)) {
          theBegin = theLimit.toDate();
        }

        const width = this.xAxisScale(theEnd) - this.xAxisScale(theBegin);
        // Use precise positioning for smooth continuous movement
        const xPosition = Math.max(width / 2 - 6, 0);
        return `translateX(${xPosition}px)`;
      });
  };

  private applyErrorOverlay() {
    if (this.canvas === null) {
      return;
    }

    if (!this.errorState.hasError) {
      clearErrorMessage(this.canvas);
      return;
    }

    applyErrorMessage(
      this.canvas,
      getErrorMessageForDisplay(
        this.errorState.messageKey,
        this.store?.state.locale,
      ),
    );
  }

  /**
   * Updates the error state based on current component conditions
   */
  private updateErrorState() {
    const previousErrorState = this.errorState.hasError;

    if (this.store === null) {
      this.errorState = getStoreUnavailableError(this.store?.state.locale);
      this.applyErrorOverlay();
      return;
    }

    if (this.store.state.error.hasError) {
      this.errorState = {
        hasError: true,
        messageKey: this.store.state.error.messageKey,
        type: this.store.state.error.type,
      };
      this.clearTimer();
      this.applyErrorOverlay();
      return;
    }

    // No error detected
    if (this.errorState.hasError) {
      this.errorState = {
        hasError: false,
        messageKey: undefined,
        type: ErrorType.None,
      };
    }

    this.applyErrorOverlay();

    // If we just recovered from an error state, restart the timer.
    // Canvas DOM is kept during errors, so the chart can resume without redraw.
    if (
      previousErrorState &&
      !this.errorState.hasError &&
      this.interval === null
    ) {
      this.timelineTimer();
    }
  }

  /**
   * Update chart data with current time to extend current values
   */
  private updateChartDataWithCurrentTime = (currentTime: dayjs.Dayjs) => {
    // Update the chart data cache with current time for items that should extend to now
    this.chartDataCache = this.chartDataCache.map((item) => {
      // If this item has end time = 0 (marker for current value), extend to current time
      if (item.end.getTime() === 0) {
        return {
          ...item,
          end: currentTime.toDate(),
        };
      }
      return item;
    });
  };

  /**
   * Rebuild chart data from stored sensor data - only called when data changes
   */
  private rebuildChartData = () => {
    const now = dayjs();
    const results: IChartDataItem[] = [];
    const theLimit = now
      .clone()
      .subtract(
        this.swimLaneSettings.scale.value,
        this.swimLaneSettings.scale.units as dayjs.ManipulateType
      );

    // Remove 'old' items first
    each(this.storedAndOrderedSensorData, (data, sensorId) => {
      const intSensorId = parseInt(sensorId, 10);

      if (indexOf(this.sensorIdsArray, intSensorId) > -1) {
        this.storedAndOrderedSensorData[`${sensorId}`] = filter(
          data,
          (sensorUpdate) => {
            const end = get(sensorUpdate, "end", +new Date());
            return theLimit.isBefore(dayjs(end));
          }
        );
      }
    });

    each(this.storedAndOrderedSensorData, (data, sensorId) => {
      const intSensorId = parseInt(sensorId, 10);

      if (indexOf(this.sensorIdsArray, intSensorId) > -1) {
        const swimlaneIndex = indexOf(this.sensorIdsArray, intSensorId);

        each(data, (sensorUpdate, index) => {
          if (isObject(sensorUpdate)) {
            const start = get(sensorUpdate, "start");
            let value = get(sensorUpdate, "value", "?");
            let end = get(sensorUpdate, "end");

            // If this is the last item in the array and has no end time, extend to now
            const isLastItem = index === data.length - 1;
            if (isLastItem && (end === undefined || end === null)) {
              end = 0; // Use 0 as marker for "current value - extend to now"
            } else if (end === undefined || end === null) {
              end = +new Date(); // Fallback for other items
            }

            // The values of the current/last stop sensors are zero based, but we need to show them one based
            if (
              isZeroBasedStopSensor(intSensorId) &&
              (typeof value === "number" || !isNaN(parseInt(String(value), 10)))
            ) {
              value =
                (typeof value === "number"
                  ? value
                  : parseInt(String(value), 10)) + 1;
            }

            results.push({
              start: new Date(start),
              end: new Date(end),
              index: swimlaneIndex,
              color: this.sensorValueColorCode(value, intSensorId),
              value: value,
            });
          }
        });
      }
    });

    this.chartDataCache = results;
    this.drawChartElements();
  };

  /**
   * Draw/redraw chart elements with current data
   */
  private drawChartElements = () => {
    const chart = d3.select(this.canvas);
    const swimlaneGroup = chart.select(".swimlanes");

    // Remove all references
    swimlaneGroup.selectAll("*").remove();

    // Create the main groups for each data item
    const itemGroups = swimlaneGroup
      .selectAll("g.mainItemGroup")
      .data(this.chartDataCache)
      .enter()
      .append("g")
      .attr("height", this.swimLaneSettings.laneHeight)
      .attr("class", "mainItemGroup");

    // First, add all rectangles (background layer)
    itemGroups
      .append("rect")
      .attr("height", () => 20)
      .style("fill", (d) => d.color);

    // Then, add text labels for numeric values and direction arrows (foreground layer)
    itemGroups
      .filter((d) => {
        // Only add text for values that should display as text (not door
        // positions, which are rendered as icons, nor the inactive state)
        const value = d.value;
        return (
          typeof value !== "string" ||
          (!LsSwimlane.DOOR_ICON_POSITIONS.includes(value) &&
            value !== MovementState.Inactive)
        );
      })
      .append("text")
      .text((d) => {
        const sensorId = this.sensorIdsArray[d.index];
        return this.getSensorDisplaySymbol(d.value, sensorId);
      })
      .attr("text-anchor", "middle")
      .attr("font-size", "9")
      .attr("font-weight", "bold")
      .attr("class", "sensor-value")
      .attr("dominant-baseline", "central")
      .attr("y", 10); // Center vertically in 20px height rectangle

    // Finally, add images for special values (top layer)
    itemGroups
      .filter(
        (d) =>
          typeof d.value === "string" &&
          LsSwimlane.DOOR_ICON_POSITIONS.includes(d.value)
      )
      .append("image")
      .attr("href", (d) => {
        let source = getAssetPath(`./assets/elevator-unknown.svg`);
        switch (d.value) {
          case DoorPosition.Open:
            source = getAssetPath(`./assets/elevator-open.svg`);
            break;
          case DoorPosition.Closed:
            source = getAssetPath(`./assets/elevator-closed.svg`);
            break;
          case DoorPosition.Locked:
            source = getAssetPath(`./assets/elevator-locked.svg`);
            break;
          case DoorPosition.Closing:
            source = getAssetPath(`./assets/elevator-closing.svg`);
            break;
          case DoorPosition.Opening:
            source = getAssetPath(`./assets/elevator-opening.svg`);
            break;
        }
        return source;
      })
      .attr("x", 0) // Start at 0, positioning will be handled by CSS transform
      .attr("y", 4) // Center vertically in 20px height rectangle (20-12)/2 = 4
      .attr("width", 12)
      .attr("height", 12)
      .style("image-rendering", "pixelated") // Ensure crisp icon rendering
      .style("shape-rendering", "crispEdges") // Prevent anti-aliasing blur
      .style("transform-origin", "center") // Set transform origin for smoother animation
      .style("will-change", "transform"); // Hint browser for optimization
  };

  /**
   * This method handles every incoming event from any sensor
   */
  private addSensorUpdateToTimeline = (
    data: { [key: number]: string | number },
    timestamp?: number
  ) => {
    if (!isNil(data)) {
      const sensorId = parseInt(keys(data)[0], 10);
      const value = values(data)[0];
      const ts = timestamp || +new Date();

      this.addData(sensorId, value, ts);
    }
  };

  private processHistoricalData = (
    sensorValues: ILiftStatusSensorResponse[]
  ) => {
    if (!isNil(values)) {
      for (const value of sensorValues) {
        const { rows, sensor } = value;
        if (!isEmpty(rows)) {
          rows.forEach((row) => {
            const update = {};
            update[sensor.id] = row.val;
            this.addSensorUpdateToTimeline(update, +new Date(row.ts));
          });
        }
      }
    }
  };

  private processLiveData = (sensorData: ILiftStatusSensorDataResponse) => {
    const dataValues = sensorData.values;
    const timestamps = sensorData.updated;

    for (let i = 0; i < keys(dataValues).length; i++) {
      const formattedDataValues = {};
      formattedDataValues[keys(dataValues)[i]] = values(dataValues)[i];
      const timestamp = timestamps[keys(dataValues)[i]];

      this.addSensorUpdateToTimeline(formattedDataValues, timestamp);
    }
  };

  private clearTimer() {
    if (this.interval !== null && this.interval !== undefined) {
      if (typeof this.interval === "number") {
        cancelAnimationFrame(this.interval);
      } else {
        window.clearInterval(this.interval);
      }
      this.interval = null;
    }
  }

  async componentWillLoad() {
    this.store = await getStore(this.idKey);
    watchStoreLocale(this, this.store, () => {
      if (!this.errorState.hasError && !this.isLoading) {
        this.drawChart();
        this.rebuildChartData();
        this.updateTimeline();
      }
    });

    // Remove back door sensor if installation does not have one
    if (this.store && !this.store.state.hasBackDoor) {
      this.sensorIdsArray = this.sensorIdsArray.filter((value) => {
        return value !== SENSOR.RearDoorPosition;
      });
    }

    this.updateErrorState();

    if (this.store !== null) {
      this.storeUnsubscribes.push(
        this.store.onChange("error", () => {
          this.updateErrorState();
        })
      );

    }

    // Store is ready before first paint; chart drawing still happens in componentDidLoad.
    this.isLoading = false;
  }

  componentDidLoad() {
    if (this.store === null) {
      return;
    }

    if (this.errorState.hasError) {
      return;
    }

    this.drawChart();

    // Fill chart with historical data
    this.processHistoricalData(this.store.state.historicalSensorData);

    // Initial chart data build
    this.rebuildChartData();

    // Start the timer to update the swim lane at regular intervals
    this.timelineTimer();

    // Subscribe to future sensor value updates
    this.storeUnsubscribes.push(
      this.store.onChange("currentSensorData", (sensorData) => {
        this.processLiveData(sensorData);
      })
    );
  }

  /**
   * Start (and stop) the animation interval
   */
  private rafErrorCount = 0;
  private readonly rafErrorThreshold = 5;
  private timelineTimer = () => {
    // Clear any existing interval or animation frame
    if (this.interval !== null && this.interval !== undefined) {
      if (typeof this.interval === "number") {
        cancelAnimationFrame(this.interval);
      } else {
        clearInterval(this.interval);
      }
    }

    // Use requestAnimationFrame for smoother animation with fallback
    const animate = () => {
      try {
        // Check for error state before updating timeline
        this.updateErrorState();

        // If there's an error, stop the animation loop
        if (this.errorState.hasError) {
          this.interval = null;
          return;
        }

        this.updateTimeline();
        this.rafErrorCount = 0; // Reset error count on success
        this.interval = requestAnimationFrame(animate);
      } catch (error) {
        this.rafErrorCount++;
        console.warn(
          "Animation frame error:",
          error,
          `(${this.rafErrorCount}/${this.rafErrorThreshold})`
        );
        if (this.rafErrorCount >= this.rafErrorThreshold) {
          console.error("Too many animation errors, stopping animation loop.");
          this.interval = null;
        } else {
          // Fallback to setInterval if requestAnimationFrame fails
          this.interval = setInterval(() => {
            try {
              // Check for error state before updating timeline
              this.updateErrorState();

              // If there's an error, stop the animation loop
              if (this.errorState.hasError) {
                clearInterval(this.interval as NodeJS.Timeout);
                this.interval = null;
                return;
              }

              this.updateTimeline();
              this.rafErrorCount = 0; // Reset error count on success
            } catch (e) {
              this.rafErrorCount++;
              console.warn(
                "setInterval animation error:",
                e,
                `(${this.rafErrorCount}/${this.rafErrorThreshold})`
              );
              if (this.rafErrorCount >= this.rafErrorThreshold) {
                console.error(
                  "Too many setInterval animation errors, stopping interval loop."
                );
                clearInterval(this.interval as NodeJS.Timeout);
                this.interval = null;
              }
            }
          }, 32); // ~30fps fallback
        }
      }
    };

    this.rafErrorCount = 0;
    this.interval = requestAnimationFrame(animate);
  };

  render() {
    return (
      <Host>
        <div
          ref={(el) => {
            this.canvas = el;
          }}
        >
          <div class="chart"></div>
        </div>
        {this.isLoading && (
          <div class="loading">{this.store.t("ui:general.loading")}</div>
        )}
        <slot></slot>
      </Host>
    );
  }

  disconnectedCallback() {
    this.clearTimer();

    // Unsubscribe from all store changes
    this.storeUnsubscribes.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.storeUnsubscribes = [];
  }
}
